import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY_PREFIX = "ab_variant_";
const WEIGHTS_CACHE_PREFIX = "ab_weights_";
const WEIGHTS_TTL_MS = 5 * 60 * 1000; // 5 minutes

type Variant = "A" | "B" | "C" | "D";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

type WeightRow = { variant: string; weight: number };

const pickWeighted = (variants: Variant[], weights: Record<string, number>): Variant => {
  const entries = variants.map((v) => [v, Math.max(0, weights[v] ?? 0)] as const);
  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  if (total <= 0) return variants[Math.floor(Math.random() * variants.length)];
  let r = Math.random() * total;
  for (const [v, w] of entries) {
    r -= w;
    if (r <= 0) return v;
  }
  return entries[entries.length - 1][0];
};

const readCachedWeights = (testName: string): Record<string, number> | null => {
  try {
    const raw = localStorage.getItem(WEIGHTS_CACHE_PREFIX + testName);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; weights: Record<string, number> };
    if (Date.now() - parsed.ts > WEIGHTS_TTL_MS) return null;
    return parsed.weights;
  } catch {
    return null;
  }
};

const writeCachedWeights = (testName: string, weights: Record<string, number>) => {
  try {
    localStorage.setItem(
      WEIGHTS_CACHE_PREFIX + testName,
      JSON.stringify({ ts: Date.now(), weights }),
    );
  } catch {
    // ignore
  }
};

const refreshWeightsInBackground = (testName: string) => {
  void supabase
    .rpc("get_variant_weights", { _test_name: testName })
    .then(({ data, error }) => {
      if (error || !data) return;
      const weights: Record<string, number> = {};
      for (const row of data as WeightRow[]) {
        weights[row.variant] = Number(row.weight) || 0;
      }
      writeCachedWeights(testName, weights);
    });
};

export const getOrAssignVariant = (
  testName: string,
  variants: Variant[] = ["A", "B", "C", "D"],
): Variant => {
  if (typeof window === "undefined") return variants[0];
  const key = STORAGE_KEY_PREFIX + testName;
  try {
    const saved = localStorage.getItem(key) as Variant | null;
    if (saved && variants.includes(saved)) {
      // Atualiza pesos em background para próximas atribuições
      refreshWeightsInBackground(testName);
      return saved;
    }
  } catch {
    // ignore
  }

  // Atribuição ponderada por performance (CTR de checkout dos últimos 7 dias)
  const cached = readCachedWeights(testName);
  let picked: Variant;
  if (cached) {
    picked = pickWeighted(variants, cached);
    // Mantém o cache fresco
    refreshWeightsInBackground(testName);
  } else {
    // Sem cache → uniforme + busca pesos para a próxima sessão
    picked = variants[Math.floor(Math.random() * variants.length)];
    refreshWeightsInBackground(testName);
  }

  try {
    localStorage.setItem(key, picked);
  } catch {
    // ignore
  }
  return picked;
};

export const trackVariantEvent = (
  event: string,
  testName: string,
  variant: Variant,
  extra: Record<string, unknown> = {},
) => {
  if (typeof window === "undefined") return;
  const payload = {
    event,
    ab_test: testName,
    [`${testName}_variant`]: variant,
    ...extra,
  };
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch {
    // ignore
  }
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", event, {
        ab_test: testName,
        variant,
        ...extra,
      });
    }
  } catch {
    // ignore
  }
};